import { useState, useRef, useEffect } from 'react'
import { supabase } from '../api/supabase'
import { useToast } from '../hooks/useToast'

interface PortLike {
  id?: string
  code: string | null
  name: string
  type?: string | null
  un_locode?: string | null
}

interface Props {
  port: PortLike | null | undefined
  onUpdated?: (updated: PortLike) => void
}

// Z23/Z33: a port missing its type's authoritative identifier silently sends malformed data to
// BoxTop and reads as ambiguous in the UI -- SEAPORT's identifier is the UN/LOCODE (the 3-letter
// `code` is an internal/BoxTop field, not shown); AIRPORT's is the IATA `code` (LOCODE is
// optional/unused for airports, so its absence isn't a warning-worthy gap). Surface it right
// where the port is picked (Ship Details tab, Job Creation Confirm dialog) instead of only in a
// sync log — click the warning to fix the ports row inline.
export default function PortLocodeWarning({ port, onUpdated }: Props) {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [locode, setLocode] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLSpanElement>(null)
  const { toast } = useToast()

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const isAirport = port?.type === 'AIRPORT'
  const missingIdentifier = isAirport ? !port?.code : !port?.un_locode

  if (!port || !port.id || !missingIdentifier) return null

  const startEdit = () => {
    setCode(port.code || '')
    setLocode(port.un_locode || '')
    setError(null)
    setOpen(true)
  }

  const save = async () => {
    const trimmedLocode = locode.trim().toUpperCase()
    if (trimmedLocode && trimmedLocode.length !== 5) { setError('LOCODE must be exactly 5 characters.'); return }

    let trimmedCode = code.trim().toUpperCase()
    if (isAirport) {
      if (!/^[A-Z]{1,3}$/.test(trimmedCode)) { setError('IATA code must be 1-3 uppercase letters.'); return }
    } else {
      if (!trimmedLocode) { setError('LOCODE is required for seaports.'); return }
      trimmedCode = trimmedLocode.slice(2)
    }
    setSaving(true); setError(null)
    try {
      const { error: err } = await supabase
        .from('ports')
        .update({ code: trimmedCode, un_locode: trimmedLocode || null })
        .eq('id', port.id!)
      if (err) throw err
      toast('Port updated', 'success')
      setOpen(false)
      onUpdated?.({ ...port, code: trimmedCode, un_locode: trimmedLocode || null })
    } catch (err: any) {
      setError(err?.message || 'Failed to update port')
    } finally {
      setSaving(false)
    }
  }

  return (
    <span ref={containerRef} className="relative inline-block ml-1 align-middle">
      <button
        type="button"
        onClick={startEdit}
        title="Missing code/LOCODE - click to update"
        className="text-amber-500 hover:text-amber-600 text-sm leading-none"
      >
        &#9650;
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 space-y-2">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">Update {port.name}</p>
          {error && <p className="text-xs text-red-500">{error}</p>}
          {isAirport ? (
            <div>
              <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">IATA Code</label>
              <input
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                maxLength={3}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
          ) : (
            <div>
              <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">LOCODE</label>
              <input
                value={locode}
                onChange={e => setLocode(e.target.value.toUpperCase())}
                maxLength={5}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setOpen(false)} className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:underline">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving} className="px-2 py-1 text-xs bg-blue-600 text-white rounded disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </span>
  )
}
